import * as React from "react"
import { cn } from "../../lib/utils"

interface SwitchProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
    checked?: boolean;
    defaultChecked?: boolean;
    onCheckedChange?: (checked: boolean) => void;
}

const Switch = React.forwardRef<HTMLButtonElement, SwitchProps>(
    ({ className, checked, defaultChecked, onCheckedChange, disabled, ...props }, ref) => {
        const [uncontrolledChecked, setUncontrolledChecked] = React.useState(defaultChecked || false);
        const isChecked = checked !== undefined ? checked : uncontrolledChecked;
        
        const handleToggle = React.useCallback(() => {
            if (disabled) return;
            
            const newChecked = !isChecked;
            if (checked === undefined) {
                setUncontrolledChecked(newChecked);
            }
            onCheckedChange?.(newChecked);
        }, [checked, isChecked, onCheckedChange, disabled]);

        return (
            <button
                type="button"
                role="switch"
                aria-checked={isChecked}
                data-state={isChecked ? "checked" : "unchecked"}
                disabled={disabled}
                onClick={handleToggle}
                ref={ref}
                className={cn(
                    "inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:bg-primary data-[state=unchecked]:bg-input",
                    className
                )}
                {...props}
            >
                <span 
                    className={cn(
                        "pointer-events-none block h-5 w-5 rounded-full bg-background shadow-lg ring-0 transition-transform data-[state=checked]:translate-x-5 data-[state=unchecked]:translate-x-0"
                    )}
                    data-state={isChecked ? "checked" : "unchecked"}
                />
            </button>
        );
    }
);

Switch.displayName = "Switch";

export { Switch };